import { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { 
  Clock, 
  Shield, 
  Award, 
  Zap, 
  HeartHandshake, 
  Droplets 
} from 'lucide-react';

const features = [
  {
    icon: Clock,
    title: 'Respuesta Rápida',
    description: 'Atendemos en menos de 24h. Urgencias disponibles.',
    color: 'bg-sky-500',
  },
  {
    icon: Shield,
    title: 'Garantía Total',
    description: 'Todos los trabajos con garantía de satisfacción.',
    color: 'bg-blue-500',
  },
  {
    icon: Award,
    title: 'Certificados',
    description: 'Equipo técnico cualificado y formado.',
    color: 'bg-cyan-500',
  },
  {
    icon: Zap,
    title: 'Eficiencia',
    description: 'Soluciones que reducen el consumo energético.',
    color: 'bg-teal-500',
  },
  {
    icon: HeartHandshake,
    title: 'Personalizado',
    description: 'Adaptamos los servicios a tus necesidades.',
    color: 'bg-indigo-500',
  },
  {
    icon: Droplets,
    title: 'Calidad',
    description: 'Tecnología avanzada para agua cristalina.',
    color: 'bg-violet-500',
  },
];

// 3D Feature Card
const FeatureCard3D = ({ feature, index }) => {
  const cardRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ['start end', 'end start'],
  });

  const row = Math.floor(index / 3);
  const col = index % 3;
  
  const rotateY = useTransform(scrollYProgress, [0, 0.5, 1], [col === 0 ? -20 : col === 2 ? 20 : 0, 0, col === 0 ? 10 : col === 2 ? -10 : 0]);
  const rotateX = useTransform(scrollYProgress, [0, 0.5, 1], [20, 0, -15]);
  const y = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [80, 0, 0, -40]);
  const scale = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.85, 1, 1, 0.95]);
  const opacity = useTransform(scrollYProgress, [0, 0.25, 0.75, 1], [0, 1, 1, 0.5]);

  const springConfig = { stiffness: 80, damping: 20 };
  const rotateYSpring = useSpring(rotateY, springConfig);
  const ySpring = useSpring(y, springConfig);

  const Icon = feature.icon;

  return (
    <motion.div
      ref={cardRef}
      style={{
        rotateY: rotateYSpring,
        rotateX,
        y: ySpring,
        scale,
        opacity,
        transformStyle: 'preserve-3d',
        perspective: 1000,
      }}
      className="group"
      data-testid={`feature-${index}`}
    >
      <div className="relative p-5 sm:p-6 lg:p-8 rounded-2xl sm:rounded-3xl bg-white border border-slate-100 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
        {/* Icon */}
        <motion.div 
          className={`w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 ${feature.color} rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 shadow-lg`}
          whileHover={{ scale: 1.1, rotate: 5 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          <Icon className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-white" />
        </motion.div>

        <h3 className="font-manrope font-bold text-lg sm:text-xl text-slate-800 mb-2 sm:mb-3">
          {feature.title}
        </h3>
        <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
          {feature.description}
        </p>

        {/* 3D glow effect */}
        <div 
          className="absolute inset-0 rounded-2xl sm:rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          style={{
            boxShadow: `0 20px 40px ${feature.color.includes('sky') ? 'rgba(14,165,233,0.2)' : feature.color.includes('blue') ? 'rgba(59,130,246,0.2)' : 'rgba(99,102,241,0.2)'}`,
          }}
        />
      </div>
    </motion.div>
  );
};

// 3D Floating Water Molecule
const WaterMolecule3D = ({ scrollYProgress, position }) => {
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 360]);
  const y = useTransform(scrollYProgress, [0, 1], [0, -300]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1.2, 0.9]);

  return (
    <motion.div
      className="absolute hidden lg:block pointer-events-none"
      style={{ ...position, rotate, y, scale }}
    >
      <svg width="80" height="60" viewBox="0 0 80 60">
        {/* Oxygen */}
        <circle cx="40" cy="30" r="18" fill="rgba(239,68,68,0.3)" stroke="rgba(239,68,68,0.5)" strokeWidth="2" />
        {/* Hydrogen 1 */}
        <circle cx="15" cy="45" r="12" fill="rgba(59,130,246,0.3)" stroke="rgba(59,130,246,0.5)" strokeWidth="2" />
        {/* Hydrogen 2 */}
        <circle cx="65" cy="45" r="12" fill="rgba(59,130,246,0.3)" stroke="rgba(59,130,246,0.5)" strokeWidth="2" />
        {/* Bonds */}
        <line x1="28" y1="40" x2="22" y2="38" stroke="rgba(148,163,184,0.5)" strokeWidth="3" />
        <line x1="52" y1="40" x2="58" y2="38" stroke="rgba(148,163,184,0.5)" strokeWidth="3" />
      </svg>
    </motion.div>
  );
};

export const WhyUs = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });

  const headerY = useTransform(scrollYProgress, [0, 0.2], [50, 0]);
  const headerOpacity = useTransform(scrollYProgress, [0, 0.15], [0, 1]);
  const headerYSpring = useSpring(headerY, { stiffness: 100, damping: 30 });

  return (
    <section 
      ref={containerRef}
      className="py-16 sm:py-24 lg:py-32 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden"
      data-testid="why-us-section"
    >
      {/* 3D Decorations */}
      <WaterMolecule3D scrollYProgress={scrollYProgress} position={{ top: '15%', left: '5%' }} />
      <WaterMolecule3D scrollYProgress={scrollYProgress} position={{ bottom: '20%', right: '8%' }} />

      {/* Background ring */}
      <motion.div 
        style={{
          rotate: useTransform(scrollYProgress, [0, 1], [0, 180]),
          y: useTransform(scrollYProgress, [0, 1], [0, -200]),
        }}
        className="absolute -top-40 -right-40 w-96 h-96 border border-sky-100 rounded-full pointer-events-none hidden lg:block"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div
          style={{ y: headerYSpring, opacity: headerOpacity }}
          className="text-center mb-10 sm:mb-16 lg:mb-20"
        >
          <motion.span
            className="inline-flex items-center gap-2 bg-white text-slate-700 px-4 py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6 shadow-sm border border-slate-100"
          >
            <Award className="w-3 h-3 sm:w-4 sm:h-4 text-sky-500" />
            Por qué elegirnos
          </motion.span>

          <h2 className="font-manrope font-extrabold text-3xl sm:text-4xl lg:text-5xl xl:text-6xl text-slate-900 mb-4 sm:mb-6 tracking-tight px-4">
            La diferencia está en{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-500 to-cyan-500">
              los detalles
            </span>
          </h2>

          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed px-4">
            Más de 15 años de experiencia nos avalan.
          </p>
        </motion.div>

        {/* Features Grid - Responsive */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <FeatureCard3D key={index} feature={feature} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyUs;
